import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { CheckCircle, XCircle } from "lucide-react";
import { FC } from "react";
import { useParams } from "react-router-dom";
import { CellProps, Column } from "react-table";

import { getUsers } from "api/users";
import { usersKeys } from "api/users/queries";
import { UsersType } from "api/users/types";
import { QueryParamsType } from "api/utils";
import { hasPermissions } from "components/stores/UserStore";
import Table from "components/Table";
import useRemoteTableLogic from "components/Table/useRemoteTableLogic";
import { useModal } from "utils/hooks/useModal";

import DeleteUserModal from "./components/DeleteUserModal";
import EditUserModal from "./components/EditUserModal";
import UsersActionsFormatter from "./formatters/UsersActionsFormatter";
import { useUsersFilter } from "./useUsersFilter";

interface UsersTableProps {
  filters?: QueryParamsType<UsersType>;
}

const UsersTable: FC<UsersTableProps> = ({ filters: filtersFromProps }) => {
  const { organisationId } = useParams<{ organisationId: string }>();
  const filterHook = useUsersFilter({
    filter: [
      {
        field: "organisation",
        operator: "is",
        value: organisationId ?? "",
      },
    ],
  });
  const filters = filtersFromProps ?? filterHook.filters;

  const {
    isOpen: isOpenDelete,
    handleClose: handleCloseDelete,
    handleOpen: handleOpenDelete,
    context: deleteContext,
  } = useModal<UsersType>();

  const {
    isOpen: isEditOpen,
    handleClose: handleCloseEdit,
    handleOpen: handleOpenEdit,
    context: editContext,
  } = useModal<UsersType>();

  const { data, status, sortOptions, paginationOptions } = useRemoteTableLogic(
    usersKeys.listFiltered(filters),
    getUsers,
    filters,
  );

  const columns: Column<UsersType>[] = [
    {
      accessor: "name",
      Header: "Name",
    },
    {
      accessor: "email",
      Header: "Email",
    },
    {
      accessor: "role",
      Header: "Role",
      Cell: ({ value }: CellProps<UsersType>) => (
        <Box textTransform="capitalize">{value}</Box>
      ),
    },
    {
      id: "confirmed",
      Header: "Status",
      Cell: ({ row }: CellProps<UsersType>) => {
        const isConfirmed = Boolean(row.original?.name?.trim());
        return (
          <Tooltip
            title={
              isConfirmed ? "Profile completed" : "Pending profile completion"
            }
            arrow
            placement="top"
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                color: (t) =>
                  isConfirmed
                    ? (t.palette.brandSuccess?.primary ?? "#2CB666")
                    : ((t.palette as any).brandText?.secondary ?? "#A2A8B6"),
              }}
            >
              {isConfirmed ? <CheckCircle size={18} /> : <XCircle size={18} />}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "inherit",
                }}
              >
                {isConfirmed ? "Confirmed" : "Pending"}
              </Typography>
            </Stack>
          </Tooltip>
        );
      },
    },
    // {
    //   accessor: "createdAt",
    //   Header: "Date"
    // },
    {
      id: "actions",
      Header: "Actions",
      Cell: ({ cell }: CellProps<UsersType>) => (
        <UsersActionsFormatter
          {...cell}
          handleOpenEdit={handleOpenEdit}
          handleOpenDelete={handleOpenDelete}
        />
      ),
      align: "right",
    },
  ];

  return (
    <Stack spacing={2} flex={1} height="100%" overflow="hidden">
      <Table
        data={data}
        columns={columns}
        sort={sortOptions}
        pagination={paginationOptions}
        status={status}
        filters={filters}
      />
      {hasPermissions("delete:users") && (
        <>
          <DeleteUserModal
            isOpen={isOpenDelete}
            handleClose={handleCloseDelete}
            user={deleteContext}
          />
        </>
      )}
      {hasPermissions("update:users") && (
        <EditUserModal
          isOpen={isEditOpen}
          handleClose={handleCloseEdit}
          user={editContext}
        />
      )}
    </Stack>
  );
};

export default UsersTable;
